import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
}

/**
 * Manual "Install App" trigger — always visible inside the user dropdown
 * so users can install on-demand even after dismissing the auto banner.
 * Hidden once the app is already running in standalone mode.
 */
export default function InstallAppButton({ className = "", onAction }) {
  const [available, setAvailable] = useState(Boolean(window.__vsmDeferredInstall));
  const [standalone, setStandalone] = useState(isStandalone());

  useEffect(() => {
    const onAvailable = () => setAvailable(true);
    const onInstalled = () => {
      setAvailable(false);
      setStandalone(true);
    };
    window.addEventListener("vsm:install-available", onAvailable);
    window.addEventListener("vsm:install-done", onInstalled);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("vsm:install-available", onAvailable);
      window.removeEventListener("vsm:install-done", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone) return null;

  const handleClick = async () => {
    if (window.__vsmDeferredInstall) {
      try {
        window.__vsmDeferredInstall.prompt();
        await window.__vsmDeferredInstall.userChoice;
      } catch (e) {
        // user cancelled
      }
      window.__vsmDeferredInstall = null;
      setAvailable(false);
      if (onAction) onAction();
      return;
    }
    // No deferred prompt — guide the user
    if (isIos()) {
      toast("On iPhone/iPad: tap the Share icon in Safari, then 'Add to Home Screen'.", {
        duration: 6000,
      });
    } else {
      toast(
        "Open the browser menu (⋮) and tap 'Install app' or 'Add to Home screen'. " +
          "Make sure you're using Chrome or Edge on Android.",
        { duration: 7000 }
      );
    }
    if (onAction) onAction();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="install-app-action"
      className={
        "flex items-center gap-2 text-sm px-3 py-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 w-full text-left " +
        className
      }
    >
      <Download className="w-4 h-4" />
      <span>Install App {available ? "" : "…"}</span>
    </button>
  );
}
